import { createFileRoute, Link } from '@tanstack/react-router';
import { useQuery } from '@tanstack/react-query';
import { formatDuration } from '@pirouette/shared';
import { supabase } from '@/lib/supabase';
import { useMediaForDancer } from '@/data/media';
import { VideoPlayer } from '@/components/media/VideoPlayer';
import { Badge } from '@/components/ui/Badge';
import { Empty } from '@/components/ui/Empty';

export const Route = createFileRoute('/parent/dancers/$id/videos')({
  component: DancerVideos,
});

function useDancer(id: string) {
  return useQuery({
    queryKey: ['dancer', id],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('dancers')
        .select('id, first_name, last_name, preferred_name')
        .eq('id', id)
        .maybeSingle();
      if (error) throw error;
      return data;
    },
  });
}

function useViewerName() {
  return useQuery({
    queryKey: ['viewer-name'],
    queryFn: async (): Promise<string> => {
      const u = await supabase.auth.getUser();
      return u.data.user?.email ?? 'viewer';
    },
  });
}

function DancerVideos() {
  const { id } = Route.useParams();
  const dancer = useDancer(id);
  const media = useMediaForDancer(id);
  const viewer = useViewerName();
  const watermark = viewer.data ? `${viewer.data} · ${new Date().toLocaleDateString()}` : undefined;

  const name = dancer.data ? (dancer.data.preferred_name ?? dancer.data.first_name) : 'Dancer';

  return (
    <div className="space-y-10">
      <header>
        <Link
          to="/parent/dancers/$id"
          params={{ id }}
          className="text-[var(--color-ink-muted)] text-[var(--text-sm)] hover:text-[var(--color-ink)]"
        >
          ← {name}
        </Link>
        <h1 className="mt-2">{name}'s videos</h1>
        <p className="mt-2 max-w-prose text-[var(--color-ink-muted)]">
          Choreography videos for dances {name} is cast in. Videos are watermarked with your account
          and time; please don't share or download.
        </p>
      </header>

      {media.isLoading && <p aria-live="polite">Loading…</p>}

      {!media.isLoading &&
        (media.data?.dances.length ?? 0) === 0 &&
        (media.data?.routines.length ?? 0) === 0 && (
          <Empty
            title="No videos yet"
            description="When choreographers upload videos for one of this dancer's pieces, they'll show here."
          />
        )}

      {media.data?.dances.map((d) => (
        <section key={d.id} aria-labelledby={`dance-${d.id}`}>
          <h2 id={`dance-${d.id}`} className="text-[var(--text-xl)]">
            {d.name}
          </h2>
          <ul className="mt-3 grid gap-4 sm:grid-cols-2">
            {d.media.map((m) => (
              <li key={m.id} className="surface-elevated grid gap-3 p-4">
                <header>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </header>
                {m.kind === 'video' && <VideoPlayer assetId={m.id} watermark={watermark} />}
                {m.description && (
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {m.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}

      {media.data?.routines.map((r) => (
        <section key={r.id} aria-labelledby={`routine-${r.id}`}>
          <h2 id={`routine-${r.id}`} className="text-[var(--text-xl)]">
            {r.name} <span className="text-[var(--color-ink-muted)]">· competition</span>
          </h2>
          <ul className="mt-3 grid gap-4 sm:grid-cols-2">
            {r.media.map((m) => (
              <li key={m.id} className="surface-elevated grid gap-3 p-4">
                <header>
                  <p className="font-medium">{m.title ?? '(untitled)'}</p>
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    <Badge tone="neutral">{m.kind}</Badge>
                    {m.duration_seconds != null && <> · {formatDuration(m.duration_seconds)}</>}
                  </p>
                </header>
                {m.kind === 'video' && <VideoPlayer assetId={m.id} watermark={watermark} />}
                {m.description && (
                  <p className="text-[var(--color-ink-muted)] text-[var(--text-sm)]">
                    {m.description}
                  </p>
                )}
              </li>
            ))}
          </ul>
        </section>
      ))}
    </div>
  );
}
